import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { ShoppingCart, User, Leaf, Star, Minus, Plus, ArrowLeft, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import ReviewSection from "../components/ReviewSection";
import WishlistButton from "../components/WishlistButton";
import Navbar from "../components/Navbar";

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        toast.error("Product not found");
        navigate("/products");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }

    setAdding(true);
    try {
      await axios.post(
        `${API}/cart/add`,
        { product_id: product.id, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Added to cart!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to cart");
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      {/* Navbar */}
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#5C5D5C] hover:text-[#1A1B1A] mb-8 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} /> Back to Products
        </button>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="aspect-square bg-white overflow-hidden mb-4">
              <img 
                src={product.images?.[selectedImage] || "https://via.placeholder.com/600"} 
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-4">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 bg-white overflow-hidden border-2 transition-colors ${
                      selectedImage === index ? "border-[#20C997]" : "border-transparent"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              {product.eco_sustainable && (
                <span className="inline-flex items-center gap-1 text-xs text-[#3E6656] bg-[#3E6656]/10 px-3 py-1.5 rounded">
                  <Leaf size={14} /> Eco Sustainable
                </span>
              )}
              <span className="text-xs text-[#F06570] uppercase tracking-wider font-medium">
                {product.category.replace("-", " ")}
              </span>
            </div>

            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A]" data-testid="product-name">
              {product.name}
            </h1>

            <div className="flex items-center gap-4 mt-4">
              {product.rating > 0 && (
                <div className="flex items-center gap-1">
                  <Star size={18} className="fill-[#D4A373] text-[#D4A373]" />
                  <span className="font-medium">{product.rating}</span>
                  <span className="text-[#5C5D5C]">({product.review_count} reviews)</span>
                </div>
              )}
              <span className="text-[#5C5D5C]">Sold by {product.vendor_name}</span>
            </div>

            <p className="text-3xl font-semibold text-[#1A1B1A] mt-6" data-testid="product-price">
              £{product.price}
            </p>

            <p className="text-[#5C5D5C] mt-6 leading-relaxed font-light" data-testid="product-description">
              {product.description}
            </p>

            {product.dimensions && (
              <div className="mt-6 p-4 bg-white border border-[#EAE6DF]">
                <h4 className="font-medium text-[#1A1B1A] mb-2">Dimensions</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  {product.dimensions.width && (
                    <div>
                      <span className="text-[#5C5D5C]">Width</span>
                      <p className="font-medium">{product.dimensions.width} cm</p>
                    </div>
                  )}
                  {product.dimensions.height && (
                    <div>
                      <span className="text-[#5C5D5C]">Height</span>
                      <p className="font-medium">{product.dimensions.height} cm</p>
                    </div>
                  )}
                  {product.dimensions.depth && (
                    <div>
                      <span className="text-[#5C5D5C]">Depth</span>
                      <p className="font-medium">{product.dimensions.depth} cm</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">
                {product.tags.map((tag, index) => (
                  <span key={index} className="text-xs bg-[#EFECE5] px-3 py-1.5 text-[#5C5D5C]">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center gap-4 mt-8">
              <div className="flex items-center border border-[#EAE6DF] bg-white">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 hover:bg-[#F7F5F0] transition-colors"
                  data-testid="quantity-decrease"
                >
                  <Minus size={18} />
                </button>
                <span className="px-6 font-medium" data-testid="quantity-display">{quantity}</span>
                <button 
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="p-3 hover:bg-[#F7F5F0] transition-colors"
                  data-testid="quantity-increase"
                >
                  <Plus size={18} />
                </button>
              </div>
              <span className="text-[#5C5D5C] text-sm">
                {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}
              </span>
            </div>

            <div className="flex gap-4 mt-8">
              <Button
                onClick={handleAddToCart}
                disabled={adding || product.stock === 0}
                className="flex-1 bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none py-6 text-base font-medium"
                data-testid="add-to-cart-btn"
              >
                {adding ? "Adding..." : "Add to Cart"}
              </Button>
              <WishlistButton productId={product.id} className="w-12 h-12 !rounded-none border border-[#EAE6DF]" />
              <Button
                onClick={() => navigate("/ai-visualizer")}
                variant="outline"
                className="border-[#20C997] text-[#20C997] rounded-none py-6"
                data-testid="visualize-btn"
              >
                Visualise in Room
              </Button>
            </div>

            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-3 text-[#5C5D5C]">
                <Check size={18} className="text-[#3E6656]" />
                <span className="text-sm">Free delivery on orders over £500</span>
              </div>
              <div className="flex items-center gap-3 text-[#5C5D5C]">
                <Check size={18} className="text-[#3E6656]" />
                <span className="text-sm">30-day returns policy</span>
              </div>
              <div className="flex items-center gap-3 text-[#5C5D5C]">
                <Check size={18} className="text-[#3E6656]" />
                <span className="text-sm">Secure checkout with Stripe</span>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <ReviewSection productId={id} />
      </div>
    </div>
  );
};

export default ProductDetailPage;
